import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const WEIGHT_STEPS = [
  {
    value: -2,
    label: "Ausschließen stark",
    shortLabel: "−2",
    tooltip: "Stark negativ — schließt das Produkt aus",
  },
  {
    value: -1,
    label: "Ausschließen leicht",
    shortLabel: "−1",
    tooltip: "Negativ — verringert die Trefferwahrscheinlichkeit",
  },
  {
    value: 0,
    label: "Neutral",
    shortLabel: "0",
    tooltip: "Neutral — keine Auswirkung auf den Score",
  },
  {
    value: 1,
    label: "Verstärken leicht",
    shortLabel: "+1",
    tooltip: "Positiv — erhöht die Trefferwahrscheinlichkeit",
  },
  {
    value: 2,
    label: "Verstärken stark",
    shortLabel: "+2",
    tooltip: "Stark positiv — präferiert das Produkt deutlich",
  },
] as const;

export type WeightStep = (typeof WEIGHT_STEPS)[number]["value"];

function stepClasses(stepValue: number, selected: boolean): string {
  if (selected) {
    if (stepValue <= -2) return "bg-red-600 text-white border-red-600";
    if (stepValue === -1) return "bg-red-100 text-red-800 border-red-400";
    if (stepValue === 0) return "bg-muted text-foreground border-muted-foreground/40";
    if (stepValue === 1) return "bg-green-100 text-green-800 border-green-400";
    return "bg-green-600 text-white border-green-600";
  }
  return "bg-background text-muted-foreground border-input hover:bg-accent";
}

interface WeightRadioGroupProps {
  value: number;
  onChange: (value: number) => void;
  name: string;
}

export function WeightRadioGroup({ value, onChange, name }: WeightRadioGroupProps) {
  return (
    <TooltipProvider delayDuration={300}>
      <div
        role="radiogroup"
        aria-label="Gewichtung"
        className="inline-flex items-center gap-1"
      >
        {WEIGHT_STEPS.map((step) => {
          const selected = value === step.value;
          return (
            <Tooltip key={step.value}>
              <TooltipTrigger asChild>
                <label
                  className={cn(
                    "cursor-pointer select-none rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                    stepClasses(step.value, selected),
                  )}
                >
                  <input
                    type="radio"
                    name={name}
                    value={step.value}
                    checked={selected}
                    onChange={() => onChange(step.value)}
                    className="sr-only"
                    aria-label={step.label}
                  />
                  <span>{step.shortLabel}</span>
                </label>
              </TooltipTrigger>
              <TooltipContent>{step.tooltip}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
